/**
 * Getting a fresh access token, once, however many callers need one.
 *
 * The app's main client refreshes expired tokens for itself, but the billing and usage
 * clients are separate instances and had no such thing, so the first request after a token
 * went stale failed outright. That is why a hard refresh could show "your plan could not be
 * loaded" while coming back to the tab a minute later worked: by then something else had
 * refreshed the token.
 *
 * Concurrent callers share one refresh rather than each starting their own, which would
 * rotate the refresh token underneath one another.
 */
import axios from 'axios';
import constants from '../../constant';

let inFlight = null;

export function currentToken() {
  return localStorage.getItem('accessToken') || '';
}

/**
 * Exchange the refresh token for a new access token. Resolves with the new token, or null
 * when there is nothing to refresh with or the exchange fails.
 */
export function refreshAccessToken() {
  if (inFlight) return inFlight;

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return Promise.resolve(null);

  inFlight = axios
    .post(
      `${constants.authApiBaseUrl}/api/v1/auth/refresh/`,
      {},
      { headers: { Authorization: `Bearer ${refreshToken}` } }
    )
    .then((res) => {
      const token = res.data && res.data.token;
      if (!token) return null;
      localStorage.setItem('accessToken', token);
      if (res.data.refreshToken) {
        localStorage.setItem('refreshToken', res.data.refreshToken);
      }
      return token;
    })
    .catch(() => null)
    .then((token) => {
      inFlight = null;
      return token;
    });

  return inFlight;
}

/**
 * Give an axios instance the same manners as the app's main client: attach the token, and
 * on a single 401 refresh it and try once more before giving up.
 */
export function withAuthRetry(instance) {
  instance.interceptors.request.use((config) => {
    const token = currentToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const original = error.config;
      const status = error.response && error.response.status;
      if (status !== 401 || !original || original._retriedAfterRefresh) {
        return Promise.reject(error);
      }
      original._retriedAfterRefresh = true;
      return refreshAccessToken().then((token) => {
        if (!token) return Promise.reject(error);
        original.headers.Authorization = `Bearer ${token}`;
        return instance(original);
      });
    }
  );

  return instance;
}
